/**
 * Minimal History-API router.
 *
 * The app was a single document with pages switched in memory (framing
 * finding F: navbar links were `href="#"`). Every public page now has a
 * real URL that also works without JavaScript because the prerender writes
 * `dist/<path>/index.html`; this module only handles in-app navigation.
 *
 * Server rendering: `setServerPath()` is called by `entry-server.tsx`
 * before `renderToString`, and `useLocationPath()` reads it through the
 * `getServerSnapshot` argument of `useSyncExternalStore`. During client
 * hydration the server snapshot is the current `location.pathname`, which
 * is the path the prerendered file was generated for.
 */

import { useSyncExternalStore } from 'react';
import { normalizePath } from './route';

let serverPath: string | null = null;
const listenerSet = new Set<() => void>();

/** Called once per render on the server (never on the client). */
export function setServerPath(path: string): void {
  serverPath = normalizePath(path);
}

function emit(): void {
  for (const listener of listenerSet) listener();
}

function subscribe(listener: () => void): () => void {
  listenerSet.add(listener);
  window.addEventListener('popstate', listener);
  return () => {
    listenerSet.delete(listener);
    window.removeEventListener('popstate', listener);
  };
}

function clientSnapshot(): string {
  return normalizePath(window.location.pathname);
}

function serverSnapshot(): string {
  if (serverPath !== null) return serverPath;
  return typeof window === 'undefined' ? '/' : clientSnapshot();
}

/** Current canonical pathname; re-renders on navigation. */
export function useLocationPath(): string {
  return useSyncExternalStore(subscribe, clientSnapshot, serverSnapshot);
}

export interface NavigateOptions {
  replace?: boolean;
  /** Keep the current scroll position (default: scroll to top) */
  keepScroll?: boolean;
}

/** Navigate within the app. Same-path calls are ignored. */
export function navigate(path: string, options: NavigateOptions = {}): void {
  const next = normalizePath(path);
  const current = normalizePath(window.location.pathname);
  const search = path.includes('?') ? path.slice(path.indexOf('?')) : '';
  if (next === current && !search && !options.replace) return;
  if (options.replace) {
    window.history.replaceState({}, '', next + search);
  } else {
    window.history.pushState({}, '', next + search);
  }
  if (!options.keepScroll) window.scrollTo(0, 0);
  emit();
}
